// Sawyer 15 workweek-hours-ot (E2) — admin client for "this week's hours"
// (GET /api/v2/admin/timesheets/workweek) and the owner's overtime-alert
// threshold (GET/PUT /api/v2/admin/payroll/settings). Kept out of api.ts so
// the Sawyer lanes' shared-file diffs stay mechanical.
//
// Deploy-window rule: the admin auto-deploys on merge while the server waits
// for a manual deploy. Every body is parsed structurally; anything that is
// not the shape below (an old server's 403, a catch-all `{}`) is a failed
// read, never a crash and never a fake 0.0 h.

import { api } from './api.js';

export type WorkweekOtBasis = 'weekly' | 'daily' | 'consecutive';

export type WorkweekStaffRow = {
  staff_user_id: string;
  employee_name: string;
  worked_minutes: number;
  scheduled_minutes: number;
  remaining_scheduled_minutes: number;
  projected_minutes: number;
  projected_overtime_minutes: number;
  projected_ot_basis: WorkweekOtBasis | null;
  on_the_clock: boolean;
  flags: string[];
};

export type WorkweekAlert = {
  staff_user_id: string;
  employee_name: string;
  level: 'over' | 'near';
  projected_minutes: number;
  projected_overtime_minutes: number;
  minutes_to_overtime: number;
};

export type WorkweekHoursView = {
  week_start: string;
  week_end: string;
  time_zone: string;
  as_of: string;
  threshold_minutes: number;
  threshold_source: 'configured' | 'default';
  weekly_ot_minutes: number;
  has_published_shifts: boolean;
  staff: WorkweekStaffRow[];
  alerts: WorkweekAlert[];
};

export type PayrollSettingsView = {
  overtime_alert_threshold_minutes: number;
  source: 'configured' | 'default';
};

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isStr = (v: unknown): v is string => typeof v === 'string';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isStaffRow(v: unknown): v is WorkweekStaffRow {
  return (
    isObj(v) &&
    isStr(v.staff_user_id) &&
    isStr(v.employee_name) &&
    isNum(v.worked_minutes) &&
    isNum(v.scheduled_minutes) &&
    isNum(v.remaining_scheduled_minutes) &&
    isNum(v.projected_minutes) &&
    isNum(v.projected_overtime_minutes) &&
    typeof v.on_the_clock === 'boolean' &&
    Array.isArray(v.flags)
  );
}

function isAlert(v: unknown): v is WorkweekAlert {
  return (
    isObj(v) &&
    isStr(v.staff_user_id) &&
    isStr(v.employee_name) &&
    (v.level === 'over' || v.level === 'near') &&
    isNum(v.projected_minutes) &&
    isNum(v.projected_overtime_minutes) &&
    isNum(v.minutes_to_overtime)
  );
}

/** Structural parse of the workweek body; null for anything else. */
export function parseWorkweekHours(body: unknown): WorkweekHoursView | null {
  if (!isObj(body)) return null;
  if (!isStr(body.week_start) || !DATE_RE.test(body.week_start)) return null;
  if (!isStr(body.week_end) || !DATE_RE.test(body.week_end)) return null;
  if (!isNum(body.threshold_minutes) || !isNum(body.weekly_ot_minutes)) return null;
  if (body.threshold_source !== 'configured' && body.threshold_source !== 'default') return null;
  if (typeof body.has_published_shifts !== 'boolean') return null;
  if (!Array.isArray(body.staff) || !body.staff.every(isStaffRow)) return null;
  if (!Array.isArray(body.alerts) || !body.alerts.every(isAlert)) return null;
  return {
    week_start: body.week_start,
    week_end: body.week_end,
    time_zone: isStr(body.time_zone) ? body.time_zone : '',
    as_of: isStr(body.as_of) ? body.as_of : '',
    threshold_minutes: body.threshold_minutes,
    threshold_source: body.threshold_source,
    weekly_ot_minutes: body.weekly_ot_minutes,
    has_published_shifts: body.has_published_shifts,
    staff: body.staff,
    alerts: body.alerts,
  };
}

export type WorkweekResult = { ok: true; data: WorkweekHoursView } | { ok: false; error: string };

/** GET /api/v2/admin/timesheets/workweek[?date=YYYY-MM-DD]. */
export async function fetchWorkweekHours(apiUrl: string, date?: string): Promise<WorkweekResult> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const res = await api.get<unknown>(apiUrl, `/api/v2/admin/timesheets/workweek${qs}`);
  if (res.error !== undefined) return { ok: false, error: res.error };
  const parsed = parseWorkweekHours(res.data);
  return parsed ? { ok: true, data: parsed } : { ok: false, error: 'unexpected_body' };
}

export type SaveSettingsResult =
  | { ok: true; data: PayrollSettingsView }
  | { ok: false; kind: 'invalid' | 'failed'; message: string };

export const THRESHOLD_INVALID_COPY = 'Pick a threshold between 1 and 40 hours, in half-hour steps.';
export const THRESHOLD_SAVE_FAILED_COPY = "Couldn't save the threshold. Try again.";

/** PUT /api/v2/admin/payroll/settings (owner-only). */
export async function savePayrollSettings(
  apiUrl: string,
  overtimeAlertThresholdMinutes: number,
): Promise<SaveSettingsResult> {
  const res = await api.put<unknown>(apiUrl, '/api/v2/admin/payroll/settings', {
    overtime_alert_threshold_minutes: overtimeAlertThresholdMinutes,
  });
  if (res.error !== undefined) {
    return res.code === 'threshold_invalid'
      ? { ok: false, kind: 'invalid', message: THRESHOLD_INVALID_COPY }
      : { ok: false, kind: 'failed', message: THRESHOLD_SAVE_FAILED_COPY };
  }
  const d = res.data;
  if (
    isObj(d) &&
    isNum(d.overtime_alert_threshold_minutes) &&
    (d.source === 'configured' || d.source === 'default')
  ) {
    return { ok: true, data: { overtime_alert_threshold_minutes: d.overtime_alert_threshold_minutes, source: d.source } };
  }
  return { ok: false, kind: 'failed', message: THRESHOLD_SAVE_FAILED_COPY };
}

/** Hours copy: 2460 → "41.0". One decimal, as the alerts read. */
export function hoursOneDecimal(minutes: number): string {
  return (minutes / 60).toFixed(1);
}

/** Threshold hours as typed: 2040 → "34", 2070 → "34.5". */
export function thresholdHoursLabel(minutes: number): string {
  const h = minutes / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

/** The AC8 alert line. */
export function overtimeAlertLine(a: WorkweekAlert): string {
  const projected = hoursOneDecimal(a.projected_minutes);
  return a.level === 'over'
    ? `${a.employee_name} is projected at ${projected} h this week, ${hoursOneDecimal(a.projected_overtime_minutes)} h of overtime.`
    : `${a.employee_name} is projected at ${projected} h this week, ${hoursOneDecimal(a.minutes_to_overtime)} h from overtime.`;
}
